import React from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

interface ErrorStateProps {
  /** Error message to display */
  message?: string;
  /** Optional title */
  title?: string | null;
  /** Retry callback function */
  onRetry?: (() => void) | null;
  /** Label for retry button */
  retryLabel?: string;
  /** Icon to display */
  icon?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Display variant */
  variant?: "default" | "inline" | "banner";
}

/**
 * ErrorState Component
 * Displays an error message with optional retry action
 */
export const ErrorState: React.FC<ErrorStateProps> = ({
  message = "Something didn't work as expected. Please try again in a moment.",
  title = null,
  onRetry = null,
  retryLabel = "Retry",
  icon = "\u274C",
  className = "",
  variant = "default",
}) => {
  if (variant === "inline") {
    return (
      <div className={cn("rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive", className)}>
        {icon && `${icon} `}
        {message}
        {onRetry && (
          <Button variant="destructive" size="sm" onClick={onRetry} className="mt-2">
            {retryLabel}
          </Button>
        )}
      </div>
    );
  }

  if (variant === "banner") {
    return (
      <div className={cn("flex items-center justify-between gap-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3", className)}>
        <span className="flex items-center gap-2 text-sm text-destructive">
          <span>{icon}</span>
          {message}
        </span>
        {onRetry && (
          <Button variant="destructive" size="sm" onClick={onRetry}>
            {retryLabel}
          </Button>
        )}
      </div>
    );
  }

  return (
    <Card className={cn("animate-scale-in rounded-xl border-destructive/20 shadow", className)}>
      <CardContent className="flex flex-col items-center justify-center gap-3 py-8 text-center">
        {icon && <div className="text-3xl">{icon}</div>}
        {title && <h3 className="text-lg font-semibold text-destructive">{title}</h3>}
        <p className="text-sm text-muted-foreground">{message}</p>
        {onRetry && (
          <Button variant="destructive" onClick={onRetry}>
            {retryLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

interface ErrorBannerProps {
  /** Error message */
  message: string;
  /** Dismiss callback */
  onDismiss?: (() => void) | null;
  /** Retry callback */
  onRetry?: (() => void) | null;
  /** Additional CSS classes */
  className?: string;
}

/**
 * ErrorBanner Component
 * A banner-style error display for page-level errors
 */
export const ErrorBanner: React.FC<ErrorBannerProps> = ({
  message,
  onDismiss = null,
  onRetry = null,
  className = "",
}) => {
  return (
    <div className={cn("flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3", className)}>
      <span className="flex items-center gap-2 text-sm text-destructive">
        <span>{"\u26A0\uFE0F"}</span>
        {message}
      </span>
      <div className="flex gap-2">
        {onRetry && (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Retry
          </Button>
        )}
        {onDismiss && (
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            Dismiss
          </Button>
        )}
      </div>
    </div>
  );
};

export default ErrorState;
